import React, { useEffect, useRef, useState } from 'react';
import Sparkline from './Sparkline';
import { COLORS, prefersReducedMotion } from './chartTheme';

// Count-up on mount / value change (skipped under prefers-reduced-motion or
// in a hidden tab, where requestAnimationFrame never fires). A timer always
// lands the final value, so the number can never stay stuck mid-animation.
function useCountUp(target, ms = 650) {
  const [shown, setShown] = useState(target);
  const prev = useRef(0);
  useEffect(() => {
    if (typeof target !== 'number' || prefersReducedMotion() || (typeof document !== 'undefined' && document.hidden)) {
      prev.current = typeof target === 'number' ? target : 0;
      setShown(target);
      return undefined;
    }
    const from = prev.current;
    prev.current = target;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      const eased = 1 - (1 - k) ** 3;
      setShown(Math.round(from + (target - from) * eased));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const settle = setTimeout(() => setShown(target), ms + 50);
    return () => { cancelAnimationFrame(raf); clearTimeout(settle); };
  }, [target, ms]);
  return shown;
}

// KPI card: number (count-up), optional delta vs the previous period, and an
// embedded sparkline of the trend.
export default function StatCard({
  label,
  value,
  display = null, // preformatted string overrides the raw number
  delta = null, // number: positive = up; deltaGoodWhen: 'up' | 'down'
  deltaGoodWhen = 'up',
  spark = null,
  color = COLORS.teal,
}) {
  const counted = useCountUp(typeof value === 'number' ? value : 0);
  const shown = display != null ? display : (typeof value === 'number' ? counted : value);
  const deltaGood = delta != null && (delta === 0 || (delta > 0) === (deltaGoodWhen === 'up'));
  return (
    <div className="ck-stat cs-card">
      <div className="ck-stat-top">
        <span className="ck-stat-label">{label}</span>
        {delta != null && delta !== 0 && (
          <span className={`ck-stat-delta ${deltaGood ? 'good' : 'bad'}`}>
            {delta > 0 ? '▲' : '▼'} {Math.abs(delta)}
          </span>
        )}
      </div>
      <div className="ck-stat-num" style={{ color }}>{shown}</div>
      {spark && spark.length > 1 && <Sparkline points={spark} color={color} width={120} height={26} />}
    </div>
  );
}
